import { create, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createContext, useContext } from 'react';
import { createStepperSlice, StepperProps, StepperSlice } from './stepper';
import { createSectionsSlice, SectionsSlice, SectionProps } from './sections';
import { createGlobalSlice, GlobalSlice } from './global';
import { createFooterSlice, FooterSlice, FooterProps } from './footer';
import {
  createSettingsOverrideSlice,
  SettingsOverrideSlice,
  SettingsOverridesProps,
} from './settingsOverride';
import { createFiltersSlice, FiltersProps, FiltersSlice } from './filters';
import {
  SlidePanelSlice,
  SlidePanelProps,
  createSlidePanelSlice,
} from './slidePanel';

interface BundleState {
  stepper: StepperSlice;
  sections: SectionsSlice;
  footer: FooterSlice;
  filters: FiltersProps;
  settingsOverride: SettingsOverrideSlice;
  global: GlobalSlice;
  slidePanel: SlidePanelSlice;
}

export interface StoreSlices {
  stepper: StepperSlice;
  sections: SectionsSlice;
  footer: FooterSlice;
  filters: FiltersSlice;
  settingsOverride: SettingsOverrideSlice;
  global: GlobalSlice;
  slidePanel: SlidePanelSlice;
}

export interface InitProps {
  stepper?: Partial<StepperProps>;
  sections?: Partial<SectionProps>;
  footer?: Partial<FooterProps>;
  settingsOverride?: Partial<SettingsOverridesProps>;
  filters?: Partial<FiltersProps>;
  global?: { isPreview: boolean };
  slidePanel?: Partial<SlidePanelProps>;
}

export const useBoundStore = (initProps: InitProps) =>
  create<StoreSlices>()(
    immer(
      devtools((...a) => {
        return {
          filters: createFiltersSlice(initProps.filters)(...a),
          stepper: createStepperSlice(initProps.stepper)(...a),
          sections: createSectionsSlice(initProps.sections)(...a),
          footer: createFooterSlice(initProps.footer)(...a),
          settingsOverride: createSettingsOverrideSlice(
            initProps.settingsOverride,
          )(...a),
          slidePanel: createSlidePanelSlice(initProps.slidePanel)(...a),
          global: createGlobalSlice(initProps.global)(...a),
        };
      }),
    ),
  );

type BundleStore = ReturnType<typeof useBoundStore>;

export const BundleContext = createContext<BundleStore | null>(null);

export function useBundleContext<T>(selector: (state: BundleState) => T): T {
  const store = useContext(BundleContext);
  if (!store) throw new Error('Missing BundleContext.Provider in the tree');
  return useStore(store, selector);
}
